<template>
  <nav class="otc-nav" :class="{ 'otc-nav-scrolled': scrolled, 'otc-nav-open': mobileOpen }">
    <div class="container otc-nav-inner">
      <a href="/" class="otc-nav-brand" aria-label="Off The Couch home">
        <img
          src="/img/otc-logo-white.png"
          alt="Off The Couch"
          class="otc-nav-brand-img"
          width="36"
          height="36"
        />
        <span class="otc-nav-brand-text">Off&nbsp;The&nbsp;Couch</span>
      </a>

      <button
        class="otc-nav-toggle"
        :aria-expanded="mobileOpen ? 'true' : 'false'"
        aria-label="Toggle navigation"
        @click="mobileOpen = !mobileOpen"
      >
        <span :class="['toggle-bar', { open: mobileOpen }]"></span>
        <span :class="['toggle-bar', { open: mobileOpen }]"></span>
        <span :class="['toggle-bar', { open: mobileOpen }]"></span>
      </button>

      <div class="otc-nav-links" :class="{ open: mobileOpen }">
        <ul>
          <li v-for="link in links" :key="link.href">
            <a
              :href="link.href"
              :target="link.external ? '_blank' : undefined"
              :rel="link.external ? 'noopener' : undefined"
              :class="['otc-nav-link', { 'router-active': isActive(link.href) }]"
              @click="mobileOpen = false"
            >
              {{ link.label }}
            </a>
          </li>
        </ul>
        <div class="otc-nav-actions">
          <a
            href="https://console.offthecouch.io/login"
            target="_blank"
            rel="noopener"
            class="otc-nav-link login"
          >
            Sign in
          </a>
          <a href="/contact" class="otc-nav-cta" @click="mobileOpen = false">
            Get a demo
          </a>
        </div>
      </div>
    </div>
  </nav>
</template>

<script>
export default {
  name: 'DashboardNavbar',
  data() {
    return {
      scrolled: false,
      mobileOpen: false,
      links: [
        { label: 'Features', href: '/features' },
        { label: 'Pricing', href: '/pricing' },
        { label: 'Updates', href: '/updates' },
        // { label: 'Blog', href: '/blog' }, // hidden until the blog ships
        { label: 'About', href: '/about' },
        { label: 'Docs', href: 'https://docs.offthecouch.io/', external: true },
      ],
    };
  },
  mounted() {
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.onScroll();
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.onScroll);
  },
  methods: {
    onScroll() {
      this.scrolled = window.scrollY > 12;
    },
    isActive(href) {
      if (href.startsWith('http')) return false;
      return this.$route.path === href;
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'src/assets/sass/marketing-tokens';

.otc-nav {
  position: fixed;
  top: 0; left: 0; right: 0;
  z-index: 1030;
  padding: 1rem 0;
  transition: background 0.2s ease, backdrop-filter 0.2s ease, border-color 0.2s ease, padding 0.2s ease;
  border-bottom: 1px solid transparent;
}

.otc-nav-scrolled,
.otc-nav-open {
  background: rgba(15, 16, 36, 0.85);
  backdrop-filter: saturate(180%) blur(14px);
  -webkit-backdrop-filter: saturate(180%) blur(14px);
  border-bottom-color: rgba(255, 255, 255, 0.06);
  padding: 0.6rem 0;
}

.otc-nav-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.otc-nav-brand {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  color: white;
  font-weight: 800;
  font-size: 1.05rem;
  letter-spacing: -0.01em;
  text-decoration: none;
  &:hover { color: white; text-decoration: none; }
}

.otc-nav-brand-img {
  width: 32px;
  height: 32px;
  object-fit: contain;
  display: block;
}

.otc-nav-brand-text { line-height: 1; }

/* Mobile toggle */

.otc-nav-toggle {
  display: none;
  background: transparent;
  border: none;
  width: 36px;
  height: 36px;
  padding: 0;
  cursor: pointer;
  position: relative;

  .toggle-bar {
    display: block;
    width: 22px;
    height: 2px;
    background: white;
    margin: 5px auto;
    transition: transform 0.2s ease, opacity 0.2s ease;
    &.open:nth-child(1) { transform: translateY(7px) rotate(45deg); }
    &.open:nth-child(2) { opacity: 0; }
    &.open:nth-child(3) { transform: translateY(-7px) rotate(-45deg); }
  }
}

.otc-nav-links {
  display: flex;
  align-items: center;
  gap: 2rem;

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    gap: 0.5rem;
  }
}

.otc-nav-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.otc-nav-link {
  display: inline-block;
  color: rgba(255, 255, 255, 0.75);
  text-decoration: none;
  font-weight: 500;
  font-size: 0.92rem;
  padding: 0.45rem 0.75rem;
  border-radius: 6px;
  transition: color 0.15s ease, background 0.15s ease;

  &:hover, &:focus {
    color: white;
    text-decoration: none;
    background: rgba(255, 255, 255, 0.05);
  }

  &.router-active {
    color: $primary;
    background: rgba(225, 78, 202, 0.1);
  }

  &.login {
    color: white;
    &:hover { color: $primary; }
  }
}

.otc-nav-cta {
  display: inline-block;
  background: $primary;
  color: white;
  padding: 0.5rem 1.1rem;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.9rem;
  text-decoration: none;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  &:hover, &:focus {
    color: white;
    transform: translateY(-1px);
    box-shadow: 0 8px 20px rgba(225, 78, 202, 0.3);
    text-decoration: none;
  }
}

@media (max-width: 991px) {
  .otc-nav-toggle { display: block; }

  .otc-nav-links {
    position: absolute;
    top: 100%;
    left: 0; right: 0;
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
    padding: 1.25rem 1.5rem 1.5rem;
    background: rgba(15, 16, 36, 0.95);
    backdrop-filter: saturate(180%) blur(14px);
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    max-height: 0;
    overflow: hidden;
    opacity: 0;
    transition: max-height 0.25s ease, opacity 0.2s ease;
    pointer-events: none;

    &.open {
      max-height: 600px;
      opacity: 1;
      pointer-events: auto;
    }

    ul {
      flex-direction: column;
      gap: 0;
    }
  }

  .otc-nav-link {
    display: block;
    padding: 0.75rem 0.5rem;
    font-size: 1rem;
    border-radius: 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.04);
    &:hover { background: transparent; color: $primary; }
  }

  .otc-nav-actions {
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
    margin-top: 0.5rem;
  }

  .otc-nav-cta {
    text-align: center;
    padding: 0.75rem 1rem;
  }
}
</style>
